import { useEffect, useRef, useState } from "react";

/**
 * Steady-rate reveal of streamed assistant text so chunky network delivery renders
 * as smooth typing instead of "blooming".
 *
 * The model/relay delivers text in large bursts (Gemini/Vertex emits ~78-char
 * deltas ~100ms apart), and the reducer commits each burst whole — so the raw
 * `message.text` jumps in visible chunks. This hook reveals from that growing
 * `target` a few characters per animation frame, accelerating to clear any backlog
 * so it never falls meaningfully behind, and SNAPS to the full text the instant
 * streaming ends — so the committed message is never lagged and turn completion is
 * never delayed. It only ever moves forward and never past `target.length`, so no
 * character is dropped or duplicated even if the reducer rewinds `target` (raw vs
 * persisted reconcile).
 */

// ~BASE chars/frame floor (≈180 cps at 60fps — faster than reading, still smooth);
// backlog is cleared within ~CATCHUP_FRAMES so a big burst catches up in ~130ms.
const BASE_CHARS_PER_FRAME = 3;
const CATCHUP_FRAMES = 8;

export function useSmoothText(target: string, isStreaming: boolean): string {
  const [revealed, setRevealed] = useState(isStreaming ? 0 : target.length);
  const targetRef = useRef(target);
  targetRef.current = target;
  const revealedRef = useRef(revealed);
  revealedRef.current = revealed;
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    // Not streaming (or just finished): show everything now, stop the loop.
    if (!isStreaming) {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      setRevealed(targetRef.current.length);
      return;
    }

    const tick = () => {
      const total = targetRef.current.length;
      let shown = revealedRef.current;
      // Reducer rewound target below what we've shown → clamp, never go backwards.
      if (shown > total) {
        shown = total;
        setRevealed(total);
      }
      if (shown < total) {
        const backlog = total - shown;
        const step = Math.max(BASE_CHARS_PER_FRAME, Math.ceil(backlog / CATCHUP_FRAMES));
        let next = Math.min(total, shown + step);
        // While more text may still arrive, don't reveal a trailing partial word —
        // avoids flashing half-written markdown (mid-`**`, half a `[link](url)` or
        // code fence). Only snap back to a boundary if one exists past `shown`, so
        // a long unbroken run can't stall.
        if (next < total) {
          const slice = targetRef.current.slice(0, next);
          const lastBreak = Math.max(slice.lastIndexOf(" "), slice.lastIndexOf("\n"));
          if (lastBreak > shown) next = lastBreak + 1;
        }
        if (next !== revealedRef.current) setRevealed(next);
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [isStreaming]);

  // While streaming, show the revealed prefix; once done, always the full text.
  return isStreaming ? target.slice(0, Math.min(revealed, target.length)) : target;
}
